import { apiFetch } from '@/stores/authStore';

export type InstitutionalDeskOrder = {
  orderId: string; symbol: string; side: 'SIDE_BUY' | 'SIDE_SELL'; state: string;
  orderQuantity: string; filledQuantity: string; remainingQuantity: string;
  price: string | null; priceScale: string; fractionalQuantityScale: string; timeInForce: string;
};
export type InstitutionalDeskOrders = {
  venue: 'polymarket_us'; protocol: 'institutional'; accountId: string;
  scope: { scopeKey: string; revision: string }; canRefresh: boolean; canCheckStatus?: boolean; canCancel: false;
  unavailableReason: string | null;
  snapshot: null | { observedAtMs: number; orders: InstitutionalDeskOrder[]; complete: true };
  lastAttempt: null | { atMs: number; state: 'ok' | 'unavailable' | 'refreshing' };
};
const object = (value: unknown): Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid order snapshot');
  return value as Record<string, unknown>;
};
const text = (value: unknown, max = 256): value is string => typeof value === 'string' && value.length > 0 && value.length <= max && !/[\x00-\x1f\x7f]/.test(value);
const decimal = (value: unknown): value is string => typeof value === 'string' && value.length <= 128 && /^(0|[1-9]\d*)(\.\d+)?$/.test(value);
const timestamp = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= 8.64e15;
const hash = (value: unknown) => typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);
const scale = (value: unknown): value is string => typeof value === 'string' && /^10{0,18}$/.test(value);

/** Validate the entire snapshot before replacing prior rows; never round native
 * integers or turn a malformed/partial reply into an empty successful read. */
export function decodeInstitutionalDeskOrders(raw: unknown, accountId: string): InstitutionalDeskOrders {
  const b = object(raw), scope = object(b.scope);
  const invalid = (): never => { throw new Error('Invalid order snapshot'); };
  if (b.venue !== 'polymarket_us' || b.protocol !== 'institutional' || b.accountId !== accountId
    || !hash(scope.scopeKey) || !hash(scope.revision) || typeof b.canRefresh !== 'boolean' || b.canCancel !== false
    || !(b.canCheckStatus === undefined || typeof b.canCheckStatus === 'boolean')
    || !(b.unavailableReason === null || (typeof b.unavailableReason === 'string' && /^US_[A-Z0-9_]{1,100}$/.test(b.unavailableReason)))) return invalid();
  let snapshot: InstitutionalDeskOrders['snapshot'] = null;
  if (b.snapshot !== null) {
    const s = object(b.snapshot);
    if (!timestamp(s.observedAtMs) || s.complete !== true || !Array.isArray(s.orders) || s.orders.length > 1_000) return invalid();
    const ids = new Set<string>();
    const orders = s.orders.map(value => {
      const o = object(value);
      if (!text(o.orderId) || !text(o.symbol) || !['SIDE_BUY', 'SIDE_SELL'].includes(String(o.side))
        || !text(o.state, 100) || !decimal(o.orderQuantity) || !decimal(o.filledQuantity) || !decimal(o.remainingQuantity)
        || !(o.price === null || decimal(o.price)) || !scale(o.priceScale) || !scale(o.fractionalQuantityScale)
        || !text(o.timeInForce, 100) || ids.has(o.orderId)) return invalid();
      ids.add(o.orderId);
      return o as InstitutionalDeskOrder;
    });
    snapshot = { observedAtMs: s.observedAtMs, orders, complete: true };
  }
  let lastAttempt: InstitutionalDeskOrders['lastAttempt'] = null;
  if (b.lastAttempt !== null) {
    const a = object(b.lastAttempt);
    if (!timestamp(a.atMs) || !['ok', 'unavailable', 'refreshing'].includes(String(a.state))) return invalid();
    lastAttempt = { atMs: a.atMs, state: a.state as 'ok' | 'unavailable' | 'refreshing' };
  }
  return { venue: 'polymarket_us', protocol: 'institutional', accountId,
    scope: { scopeKey: scope.scopeKey as string, revision: scope.revision as string },
    canRefresh: b.canRefresh, canCheckStatus: b.canCheckStatus === true, canCancel: false, unavailableReason: b.unavailableReason as string | null, snapshot, lastAttempt };
}

export class InstitutionalDeskReadError extends Error {
  constructor(readonly accessChanged: boolean) { super('Institutional orders unavailable'); }
}
export async function readInstitutionalDeskOrders(accountId: string, signal: AbortSignal,
  refresh?: InstitutionalDeskOrders['scope']): Promise<InstitutionalDeskOrders> {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(accountId)) throw new InstitutionalDeskReadError(true);
  const response = await apiFetch(`/api/polymarket-us/accounts/${accountId}/institutional-orders${refresh ? '/refresh' : ''}`,
    { method: refresh ? 'POST' : 'GET', cache: 'no-store', signal,
      ...(refresh ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(refresh) } : {}) });
  if (!response.ok) {
    // A concurrent refresh preserves the last authorized snapshot. Only an
    // actual authority/context change invalidates those saved rows.
    const error = response.status === 409 ? await response.json().catch(() => null) : null;
    throw new InstitutionalDeskReadError([401, 403, 404].includes(response.status)
      || (response.status === 409 && error?.code !== 'US_INSTITUTIONAL_ORDERS_BUSY'));
  }
  return decodeInstitutionalDeskOrders(await response.json(), accountId);
}
